import { Result, Omit, resultFailure, resultSuccess } from '../common';
import { BuilderDocument, VideoLayer, BuilderDocumentInspector } from '../builderDocument';
import { UploadId } from '../upload';
import { AssetIdAndLayerId, BuilderTemplateId } from '../builderTemplate';
import { WorkflowStartResult } from '../external';
import { BaseSocketClientService } from '../sockets';
import { ISize } from '../geometry';
import {
    IProcessVideoProgress,
    IPublishVideoRequest,
    IVideoRequestData,
    VideoProcessingFailedEvent,
    VideoProcessingFinishedEvent,
    VideoProcessingProgressEvent,
} from './video';

export type PreparedVideoRequestData = Omit<IVideoRequestData, 'image'>;
export type PublishVideoCallback<CD> = (request: IPublishVideoRequest<CD>) => PromiseLike<WorkflowStartResult>;

export interface VideoProcessingContext {
    isProcessingOnServer: boolean;
    videoDuration: number; // Set later
    timeMark: number;
}

export interface VideoUploadResult {
    uploadId: UploadId | null;
    existingUrl: string | null;
}

export enum CheckVideoRequirementsError {
    NoVideoLayers,
    NoVideoUploaded,
}

export abstract class BaseVideoPublishService<TContext> {
    protected readonly inspector = new BuilderDocumentInspector();

    public constructor(protected readonly socketService: BaseSocketClientService) {}

    public createVideoRequestData(
        doc: BuilderDocument,
        targetDimensions: ISize,
        imageUploadId: UploadId,
        videoUpload: VideoUploadResult,
    ): IVideoRequestData {
        return {
            ...this.createPreparedVideoRequestData(doc, targetDimensions, videoUpload),
            image: imageUploadId,
        };
    }

    public async prepareVideo(
        doc: BuilderDocument,
        targetDimensions: ISize,
        context: TContext,
    ): Promise<PreparedVideoRequestData> {
        const videoLayer = await this.checkVideoRequirementsAsPromise(doc);
        const videoUpload = await this.uploadVideo(videoLayer, context);

        return this.createPreparedVideoRequestData(doc, targetDimensions, videoUpload);
    }

    public async prepareVideoWithOverlay(
        doc: BuilderDocument,
        dimensions: ISize,
        context: TContext,
    ): Promise<IVideoRequestData> {
        try {
            const videoLayer = await this.checkVideoRequirementsAsPromise(doc);
            const imageUploadId = await this.createVideoOverlayAndUpload(doc, dimensions, context);
            const videoUpload = await this.uploadVideo(videoLayer, context);

            return this.createVideoRequestData(doc, dimensions, imageUploadId, videoUpload);
        } catch (error) {
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
            return Promise.reject(error);
        }
    }

    // eslint-disable-next-line max-params
    public publishPreparedVideo<TChannelData>(
        videoData: IVideoRequestData,
        videoProcessingCallbacks: {
            onProcessingStateChange: (isProcessing: boolean) => void;
            onProgress: (result: IProcessVideoProgress) => void;
        },
        templateId: BuilderTemplateId,
        plannerId: number | null,
        channelData: TChannelData,
        publishCallback: PublishVideoCallback<TChannelData>,
        linkedAssetLibraryAssetIds: AssetIdAndLayerId[],
    ): PromiseLike<string> {
        const requestBody: IPublishVideoRequest<any> = {
            auditData: {
                builderTemplateId: templateId,
                linkedAssetLibraryAssetIds,
                plannerId: plannerId || undefined,
            },
            channelData,
            videoData,
        };
        videoProcessingCallbacks.onProcessingStateChange(true);
        return this.socketService
            .startWorkflow(
                () => publishCallback(requestBody),
                VideoProcessingFinishedEvent,
                VideoProcessingFailedEvent,
                {
                    onProgress: videoProcessingCallbacks.onProgress,
                    progressEvent: VideoProcessingProgressEvent,
                },
            )
            .then(
                data => {
                    videoProcessingCallbacks.onProcessingStateChange(false);
                    return data.videoUrl;
                },
                err => {
                    videoProcessingCallbacks.onProcessingStateChange(false);
                    throw err;
                },
            );
    }

    // eslint-disable-next-line max-params
    public publishVideoWithListeners<TChannelData>(
        doc: BuilderDocument,
        dimensions: ISize,
        context: TContext,
        videoProcessingCallbacks: {
            onProcessingStateChange: (isProcessing: boolean) => void;
            onProgress: (result: IProcessVideoProgress) => void;
        },
        templateId: BuilderTemplateId,
        plannerId: number | null,
        channelData: TChannelData,
        publishCallback: PublishVideoCallback<TChannelData>,
        linkedAssetLibraryAssetIds: AssetIdAndLayerId[],
    ): PromiseLike<string> {
        return this.prepareVideoWithOverlay(doc, dimensions, context).then((videoData: IVideoRequestData) =>
            this.publishPreparedVideo(
                videoData,
                videoProcessingCallbacks,
                templateId,
                plannerId,
                channelData,
                publishCallback,
                linkedAssetLibraryAssetIds,
            ),
        );
    }

    protected checkVideoRequirements(doc: BuilderDocument): Result<CheckVideoRequirementsError, VideoLayer> {
        const videoLayers = this.inspector.getVideoLayers(doc);

        if (videoLayers.length === 0) {
            return resultFailure(CheckVideoRequirementsError.NoVideoLayers);
        }
        const layer = videoLayers[0]; // Only get the first video for now.

        if (layer.location === null) {
            return resultFailure(CheckVideoRequirementsError.NoVideoUploaded);
        }

        return resultSuccess(layer);
    }

    private createPreparedVideoRequestData(
        doc: BuilderDocument,
        targetDimensions: ISize,
        videoUpload: VideoUploadResult,
    ): PreparedVideoRequestData {
        return {
            dimensions: targetDimensions,
            removeAudio: this.inspector.shouldRemoveAudioForVideoOutput(doc),
            videoUploadId: videoUpload.uploadId,
            videoUrl: videoUpload.existingUrl,
        };
    }

    protected abstract createVideoOverlayAndUpload(
        doc: BuilderDocument,
        dimensions: ISize,
        context: TContext,
    ): PromiseLike<UploadId>;

    protected abstract uploadVideo(videoLayer: VideoLayer, context: TContext): PromiseLike<VideoUploadResult>;

    public abstract checkVideoRequirementsAsPromise(doc: BuilderDocument): PromiseLike<VideoLayer>;
}
